import { ArrowDownUp, ChevronLeft, ChevronRight, Plus, Search, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from '../../assets/scss/pages/SuperAdminShops.module.scss';
import SuperAdminLayout from '../../components/superadmin/SuperAdminLayout';
import { useHeaderSlot } from '../../contexts/SuperAdminHeaderContext';
import api from '../../config/axios-conf';
import { useToastContext } from '../../contexts/ToastContext';
import { formatPhoneNumber } from '../../utils/formatters';
import { MoneyInput, PhoneInput } from '../../components/common/FormattedInput';

const LIMIT = 20;

// Ô tìm kiếm header giữ state nội bộ → không bị dựng lại element qua setSlot mỗi lần gõ
// (tránh ngắt phiên composition của bộ gõ tiếng Việt làm mất/nhảy chữ).
function HeaderSearchInput({ onSearch, onEnter, placeholder }) {
    const [value, setValue] = useState('');
    const handleChange = (e) => {
        const v = e.target.value;
        setValue(v);
        onSearch(v);
    };
    return (
        <div className={styles.hSearchWrap}>
            <Search size={14} />
            <input
                className={styles.hSearchInput}
                placeholder={placeholder}
                value={value}
                onChange={handleChange}
                onKeyDown={(e) => e.key === 'Enter' && onEnter?.()}
            />
        </div>
    );
}

function ShopsManagementContent() {
    const { showSuccess, showError } = useToastContext();
    const { setSlot } = useHeaderSlot();
    const [searchParams] = useSearchParams();
    const [shops, setShops] = useState([]);
    const [deletedShops, setDeletedShops] = useState([]);
    const [activeTab, setActiveTab] = useState('active');
    const searchTermRef = useRef('');
    const [sortBy, setSortBy] = useState('createdAt');
    const [order, setOrder] = useState('DESC');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingShop, setEditingShop] = useState(null);
    const [formData, setFormData] = useState({
        nameAdmin: '', nameShop: '', phoneOne: '', phoneTwo: '',
        address: '', email: '', timeStart: '', timeEnd: '', payed: '', isActive: true,
    });

    const fetchShops = useCallback(async (targetPage) => {
        try {
            setIsLoading(true);
            const token = localStorage.getItem('token');
            const filter = searchParams.get('filter');

            if (filter === 'revenue') {
                const { data } = await api.get('/superadmin/shops?filter=revenue', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setShops(Array.isArray(data) ? data : []);
                setTotal(0);
                return;
            }

            const params = new URLSearchParams();
            if (debouncedSearch) params.set('search', debouncedSearch);
            if (activeTab === 'active') params.set('isActive', 'true');
            if (activeTab === 'inactive') params.set('isActive', 'false');
            params.set('sortBy', sortBy);
            params.set('order', order);
            params.set('page', targetPage || 1);
            params.set('limit', LIMIT);

            const { data } = await api.get(`/superadmin/shops?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (data && data.data) {
                setShops(data.data);
                setTotal(data.total || 0);
            } else {
                setShops(Array.isArray(data) ? data : []);
                setTotal(0);
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Không thể tải danh sách shops';
            setError(msg);
            showError(msg);
        } finally {
            setIsLoading(false);
        }
    }, [searchParams, debouncedSearch, activeTab, sortBy, order, showError]);

    const fetchDeletedShops = useCallback(async () => {
        try {
            setIsLoading(true);
            const token = localStorage.getItem('token');
            const { data } = await api.get('/superadmin/shops/deleted/list', {
                headers: { Authorization: `Bearer ${token}` },
            });
            setDeletedShops(data);
        } catch (err) {
            const msg = err.response?.data?.message || 'Không thể tải danh sách shops đã xóa';
            setError(msg);
            showError(msg);
        } finally {
            setIsLoading(false);
        }
    }, [showError]);

    const handleSearchChange = useCallback((v) => {
        searchTermRef.current = v;
    }, []);
    const handleSearch = useCallback(() => {
        setDebouncedSearch(searchTermRef.current.trim());
        setPage(1);
    }, []);

    // Reset page về 1 khi thay đổi filter/sort (không phải search)
    useEffect(() => {
        setPage(1);
    }, [activeTab, sortBy, order]);

    useEffect(() => {
        if (activeTab === 'deleted') fetchDeletedShops();
        else fetchShops(page);
    }, [activeTab, page, debouncedSearch, fetchShops, fetchDeletedShops]);

    // ===== Handlers =====
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') return setFormData((p) => ({ ...p, [name]: checked }));
        setFormData((p) => ({ ...p, [name]: value }));
    };

    const setField = (name, value) => setFormData((p) => ({ ...p, [name]: value }));

    const openCreate = useCallback(() => {
        setEditingShop(null);
        setFormData({ nameAdmin: '', nameShop: '', phoneOne: '', phoneTwo: '', address: '', email: '', timeStart: '', timeEnd: '', payed: '', isActive: true });
        setShowModal(true);
    }, []);

    // ===== Header slot =====
    useEffect(() => {
        const SORT_LABELS = { createdAt: 'Ngày tạo', lastLoginAt: 'Đăng nhập', lastOrderAt: 'Tạo đơn', nameShop: 'Tên shop', timeEnd: 'Ngày hết hạn', code: 'Mã shop' };
        const tabs = [
            { key: 'active', label: 'Hoạt động' },
            { key: 'inactive', label: 'Bị khóa' },
            { key: 'deleted', label: `Đã xóa (${deletedShops.length})` },
        ];
        setSlot(
            <div className={styles.headerSlot}>
                {tabs.map((t) => (
                    <button
                        key={t.key}
                        className={`${styles.hTab}${activeTab === t.key ? ` ${styles.hTabActive}` : ''}`}
                        onClick={() => { setActiveTab(t.key); searchTermRef.current = ''; setDebouncedSearch(''); }}
                    >{t.label}</button>
                ))}
                <div className={styles.hDivider} />
                <HeaderSearchInput
                    key={activeTab}
                    onSearch={handleSearchChange}
                    onEnter={handleSearch}
                    placeholder="Tìm kiếm..."
                />
                <button className={styles.hIconBtn} onClick={handleSearch} title="Tìm kiếm">
                    <Search size={15} />
                </button>
                {activeTab !== 'deleted' && (
                    <>
                        <div className={styles.hDivider} />
                        <select className={styles.hSelect} value={sortBy} onChange={(e) => setSortBy(e.target.value)} title="Sắp xếp theo">
                            {Object.entries(SORT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                        <button className={styles.hIconBtn} onClick={() => setOrder((o) => o === 'DESC' ? 'ASC' : 'DESC')} title={order === 'DESC' ? 'Mới nhất' : 'Cũ nhất'}>
                            <ArrowDownUp size={15} />
                        </button>
                        <div className={styles.hDivider} />
                        <button className={styles.hBtnPrimary} onClick={openCreate}>
                            <Plus size={15} /> Tạo mới
                        </button>
                    </>
                )}
            </div>
        );
        return () => setSlot(null);
    }, [activeTab, sortBy, order, deletedShops.length, handleSearch, handleSearchChange, openCreate, setSlot]);

    const openEdit = (shop) => {
        setEditingShop(shop);
        const rawPhone = (p) => p ? p.toString().replace(/\D/g, '') : '';
        const payedNum = typeof shop.payed === 'string' ? parseFloat(shop.payed) : shop.payed;
        setFormData({
            nameAdmin: shop.nameAdmin || '',
            nameShop: shop.nameShop || '',
            phoneOne: rawPhone(shop.phoneOne),
            phoneTwo: rawPhone(shop.phoneTwo),
            address: shop.address || '',
            email: shop.email || '',
            timeStart: shop.timeStart ? shop.timeStart.split('T')[0] : '',
            timeEnd: shop.timeEnd ? shop.timeEnd.split('T')[0] : '',
            payed: !isNaN(payedNum) ? Math.floor(payedNum).toString() : '0',
            isActive: shop.isActive !== undefined ? shop.isActive : true,
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const submitData = {
                ...formData,
                phoneOne: formData.phoneOne.replace(/\s/g, ''),
                phoneTwo: formData.phoneTwo.replace(/\s/g, ''),
                timeStart: formData.timeStart ? new Date(formData.timeStart) : null,
                timeEnd: formData.timeEnd ? new Date(formData.timeEnd) : null,
                payed: formData.payed ? parseFloat(formData.payed.replace(/\./g, '')) || 0 : 0,
            };
            if (editingShop) {
                await api.put(`/superadmin/shops/${editingShop.id}`, submitData, { headers: { Authorization: `Bearer ${token}` } });
                showSuccess('Cập nhật shop thành công!');
            } else {
                await api.post('/superadmin/shops', submitData, { headers: { Authorization: `Bearer ${token}` } });
                showSuccess('Tạo shop mới thành công!');
            }
            setShowModal(false);
            setError('');
            fetchShops(page);
        } catch (err) {
            const msg = err.response?.data?.message || 'Có lỗi xảy ra';
            setError(msg);
            showError(msg);
        }
    };

    const handleToggleStatus = async (shopId) => {
        try {
            const token = localStorage.getItem('token');
            await api.put(`/superadmin/shops/${shopId}/toggle-status`, {}, { headers: { Authorization: `Bearer ${token}` } });
            showSuccess('Cập nhật trạng thái thành công!');
            fetchShops(page);
        } catch (err) {
            showError(err.response?.data?.message || 'Có lỗi xảy ra');
        }
    };

    const handleSoftDelete = async (shopId) => {
        if (!window.confirm('Xóa mềm shop này? Shop sẽ chuyển vào danh sách đã xóa.')) return;
        try {
            const token = localStorage.getItem('token');
            await api.put(`/superadmin/shops/${shopId}/soft-delete`, {}, { headers: { Authorization: `Bearer ${token}` } });
            showSuccess('Xóa shop thành công!');
            fetchShops(page);
        } catch (err) {
            showError(err.response?.data?.message || 'Có lỗi xảy ra');
        }
    };

    const handleRestore = async (shopId) => {
        if (!window.confirm('Khôi phục shop này?')) return;
        try {
            const token = localStorage.getItem('token');
            await api.put(`/superadmin/shops/${shopId}/restore`, {}, { headers: { Authorization: `Bearer ${token}` } });
            showSuccess('Khôi phục thành công!');
            fetchDeletedShops();
        } catch (err) {
            showError(err.response?.data?.message || 'Có lỗi xảy ra');
        }
    };

    const handleExtendOneYear = async (shop) => {
        const current = shop.timeEnd ? new Date(shop.timeEnd) : new Date();
        const newDate = new Date(current);
        newDate.setFullYear(newDate.getFullYear() + 1);
        const formatted = newDate.toLocaleDateString('vi-VN');
        if (!window.confirm(`Gia hạn shop "${shop.nameShop}" thêm 1 năm?\nNgày kết thúc mới: ${formatted}`)) return;
        try {
            const token = localStorage.getItem('token');
            await api.put(`/superadmin/shops/${shop.id}`, { timeEnd: newDate }, { headers: { Authorization: `Bearer ${token}` } });
            showSuccess(`Đã gia hạn đến ${formatted}`);
            fetchShops(page);
        } catch (err) {
            showError(err.response?.data?.message || 'Có lỗi xảy ra');
        }
    };

    const handlePermanentDelete = async (shopId) => {
        if (!window.confirm('Xóa vĩnh viễn shop này? Không thể hoàn tác!')) return;
        try {
            const token = localStorage.getItem('token');
            await api.delete(`/superadmin/shops/${shopId}`, { headers: { Authorization: `Bearer ${token}` } });
            showSuccess('Xóa vĩnh viễn thành công!');
            fetchDeletedShops();
        } catch (err) {
            showError(err.response?.data?.message || 'Có lỗi xảy ra');
        }
    };

    const getDaysUntilExpiry = (timeEnd) => {
        if (!timeEnd) return null;
        const diff = new Date(timeEnd) - new Date();
        return Math.ceil(diff / (1000 * 60 * 60 * 24));
    };

    const normalize = (str) =>
        (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();

    const filteredDeletedShops = deletedShops.filter((s) => {
        if (!debouncedSearch) return true;
        const t = normalize(debouncedSearch);
        return normalize(s.code?.toString()).includes(t) || normalize(s.nameShop).includes(t) || normalize(s.nameAdmin).includes(t);
    });

    const totalPages = Math.ceil(total / LIMIT);

    const renderPageButtons = () => {
        const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1);

        const result = [];
        pages.forEach((p, idx) => {
            if (idx > 0 && p - pages[idx - 1] > 1) {
                result.push(<span key={`ellipsis-${p}`} className={styles.pageBtn} style={{ border: 'none', cursor: 'default' }}>…</span>);
            }
            result.push(
                <button
                    key={p}
                    className={`${styles.pageBtn}${p === page ? ` ${styles.pageBtnActive}` : ''}`}
                    onClick={() => setPage(p)}
                >
                    {p}
                </button>
            );
        });
        return result;
    };

    if (isLoading) {
        return (
            <div className={styles.loading}>
                <div className={styles.spinner} /> Đang tải...
            </div>
        );
    }

    return (
        <div className={styles.page}>
            {error && <div className={styles.error}>{error}</div>}

            {/* Table */}
            <div className={styles.tableCard}>
                {activeTab !== 'deleted' ? (
                    <>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>ID Shop</th>
                                    <th>Shop</th>
                                    <th>Chủ shop</th>
                                    <th>SĐT</th>
                                    <th>Đăng nhập gần nhất</th>
                                    <th>Tạo đơn gần nhất</th>
                                    <th>Trạng thái</th>
                                    <th>Thao tác</th>
                                </tr>
                            </thead>
                            <tbody>
                                {shops.length === 0 ? (
                                    <tr className={styles.emptyRow}>
                                        <td colSpan="8">Không có shop nào {activeTab === 'active' ? 'đang hoạt động' : 'bị khóa'}</td>
                                    </tr>
                                ) : shops.map((shop) => (
                                    <tr key={shop.id}>
                                        <td>{shop.id}</td>
                                        <td>
                                            <div className={styles.shopInfo}>
                                                <span className={styles.shopName}>{shop.nameShop}</span>
                                                <span className={styles.shopCode}>#{shop.code}</span>
                                            </div>
                                        </td>
                                        <td>{shop.nameAdmin}</td>
                                        <td>{formatPhoneNumber(shop.phoneOne)}</td>
                                        <td>{shop.lastLoginAt ? new Date(shop.lastLoginAt).toLocaleString('vi-VN') : '—'}</td>
                                        <td>{shop.lastOrderAt ? new Date(shop.lastOrderAt).toLocaleString('vi-VN') : '—'}</td>
                                        <td>
                                            <div className={styles.statusCell}>
                                                <span className={`${styles.badge} ${shop.isActive ? styles.badgeActive : styles.badgeInactive}`}>
                                                    {shop.isActive ? 'Hoạt động' : 'Tạm khóa'}
                                                </span>
                                                {sortBy === 'timeEnd' && (() => {
                                                    const days = getDaysUntilExpiry(shop.timeEnd);
                                                    if (days === null) return null;
                                                    if (days <= 0) return <span className={`${styles.badge} ${styles.badgeExpired}`}>Đã hết hạn</span>;
                                                    return (
                                                        <span className={`${styles.badge} ${days <= 15 ? styles.badgeExpiringSoon : styles.badgeExpiringWarn}`}>
                                                            {days} ngày
                                                        </span>
                                                    );
                                                })()}
                                            </div>
                                        </td>
                                        <td>
                                            <div className={styles.actions}>
                                                <button className={`${styles.btnSmall} ${styles.btnEdit}`} onClick={() => openEdit(shop)}>Sửa</button>
                                                <button className={`${styles.btnSmall} ${styles.btnSuccess}`} onClick={() => handleExtendOneYear(shop)}>+1 năm</button>
                                                <button className={`${styles.btnSmall} ${shop.isActive ? styles.btnToggleOn : styles.btnToggleOff}`} onClick={() => handleToggleStatus(shop.id)}>
                                                    {shop.isActive ? 'Khóa' : 'Mở'}
                                                </button>
                                                <button className={`${styles.btnSmall} ${styles.btnDanger}`} onClick={() => handleSoftDelete(shop.id)}>Xóa</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {totalPages > 1 && (
                            <div className={styles.pagination}>
                                <span className={styles.paginationInfo}>
                                    {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)} / {total} shops
                                </span>
                                <div className={styles.paginationButtons}>
                                    <button className={styles.pageBtn} disabled={page === 1} onClick={() => setPage(page - 1)}>
                                        <ChevronLeft />
                                    </button>
                                    {renderPageButtons()}
                                    <button className={styles.pageBtn} disabled={page === totalPages} onClick={() => setPage(page + 1)}>
                                        <ChevronRight />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>ID Shop</th>
                                <th>Shop</th>
                                <th>Chủ shop</th>
                                <th>SĐT</th>
                                <th>Ngày xóa</th>
                                <th>Thao tác</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDeletedShops.length === 0 ? (
                                <tr className={styles.emptyRow}>
                                    <td colSpan="6">Không có shop nào đã xóa</td>
                                </tr>
                            ) : filteredDeletedShops.map((shop) => (
                                <tr key={shop.id}>
                                    <td>{shop.id}</td>
                                    <td>
                                        <div className={styles.shopInfo}>
                                            <span className={styles.shopName}>{shop.nameShop}</span>
                                            <span className={styles.shopCode}>#{shop.code}</span>
                                        </div>
                                    </td>
                                    <td>{shop.nameAdmin}</td>
                                    <td>{formatPhoneNumber(shop.phoneOne)}</td>
                                    <td>{shop.deletedAt ? new Date(shop.deletedAt).toLocaleString('vi-VN') : '—'}</td>
                                    <td>
                                        <div className={styles.actions}>
                                            <button className={`${styles.btnSmall} ${styles.btnSuccess}`} onClick={() => handleRestore(shop.id)}>Khôi phục</button>
                                            <button className={`${styles.btnSmall} ${styles.btnDanger}`} onClick={() => handlePermanentDelete(shop.id)}>Xóa vĩnh viễn</button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
                    <div className={styles.modal}>
                        <div className={styles.modalHeader}>
                            <h2>{editingShop ? 'Chỉnh sửa Shop' : 'Tạo Shop mới'}</h2>
                            <button className={styles.modalClose} onClick={() => setShowModal(false)}>
                                <X />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className={styles.modalBody}>
                                <div className={styles.formGrid}>
                                    <div className={styles.formGroup}>
                                        <label>Tên Chủ Shop *</label>
                                        <input className={styles.input} name="nameAdmin" value={formData.nameAdmin} onChange={handleInputChange} required />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Tên Shop *</label>
                                        <input className={styles.input} name="nameShop" value={formData.nameShop} onChange={handleInputChange} required />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>SĐT 1 *</label>
                                        <PhoneInput className={styles.input} value={formData.phoneOne} onChange={(raw) => setField('phoneOne', raw)} placeholder="0123 456 789" required />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>SĐT 2</label>
                                        <PhoneInput className={styles.input} value={formData.phoneTwo} onChange={(raw) => setField('phoneTwo', raw)} placeholder="0123 456 789" />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Email</label>
                                        <input className={styles.input} type="email" name="email" value={formData.email} onChange={handleInputChange} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Địa chỉ</label>
                                        <input className={styles.input} name="address" value={formData.address} onChange={handleInputChange} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Ngày bắt đầu</label>
                                        <input className={styles.input} type="date" name="timeStart" value={formData.timeStart} onChange={handleInputChange} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Ngày kết thúc</label>
                                        <input className={styles.input} type="date" name="timeEnd" value={formData.timeEnd} onChange={handleInputChange} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Số tiền đã thanh toán</label>
                                        <div className={styles.inputCurrencyWrap}>
                                            <MoneyInput className={styles.input} value={formData.payed} onChange={(raw) => setField('payed', raw)} placeholder="0" />
                                            <span>đ</span>
                                        </div>
                                    </div>
                                    <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                                        <label className={styles.checkboxLabel}>
                                            <input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleInputChange} />
                                            Shop đang hoạt động
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div className={styles.modalFooter}>
                                <button type="button" className={styles.btnCancel} onClick={() => setShowModal(false)}>Hủy</button>
                                <button type="submit" className={styles.btnPrimary}>
                                    {editingShop ? 'Cập nhật' : 'Tạo mới'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function ShopsManagement() {
    return (
        <SuperAdminLayout>
            <ShopsManagementContent />
        </SuperAdminLayout>
    );
}
